import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import i18n from 'i18n-js';
import { PrintConfig, PrinterModel } from 'store/categoryStore/reducer';
import { useDispatch } from 'react-redux';
import { actionCreators } from 'store/categoryStore/actionCreators';

const PRINTER_MODEL_OPTIONS: PrinterModel[] = ['TM-T30III', 'TM-U220II'];

interface SetCategoryPrinterConfigurationsProps {
    open: boolean;
    onClose: () => void;
    categoryId?: string;
    initialPrintConfigs?: PrintConfig[];
}

export default function SetCategoryPrinterConfigurations({
    open,
    onClose,
    categoryId,
    initialPrintConfigs = [],
}: SetCategoryPrinterConfigurationsProps) {
    const [printConfigs, setPrintConfigs] = useState<PrintConfig[]>([]);
    const [printConfigErrors, setPrintConfigErrors] = useState<{
        [key: number]: { printerModel?: string; printerIp?: string };
    }>({});
    const [initialConfigs, setInitialConfigs] = useState<PrintConfig[]>([]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (open) {
            const configs = [...initialPrintConfigs];
            setPrintConfigs(configs);
            setInitialConfigs(JSON.parse(JSON.stringify(configs)));
            setPrintConfigErrors({});
        }
    }, [open, initialPrintConfigs]);

    function addPrintConfig() {
        const newPrintConfig: PrintConfig = {
            deviceId: '',
            printerModel: undefined,
            printerIp: '',
        };
        setPrintConfigs([...printConfigs, newPrintConfig]);
    }

    function removePrintConfig(index: number) {
        const newConfigs = printConfigs.filter((_, i) => i !== index);
        setPrintConfigs(newConfigs);

        const updatedErrors = { ...printConfigErrors };
        delete updatedErrors[index];
        const reindexedErrors: typeof printConfigErrors = {};
        Object.keys(updatedErrors).forEach((key) => {
            const numKey = Number(key);
            if (numKey > index) {
                reindexedErrors[numKey - 1] = updatedErrors[numKey];
            } else {
                reindexedErrors[numKey] = updatedErrors[numKey];
            }
        });
        setPrintConfigErrors(reindexedErrors);
    }

    function updatePrintConfig(index: number, field: keyof PrintConfig, value: any) {
        const newConfigs = [...printConfigs];
        newConfigs[index] = { ...newConfigs[index], [field]: value };
        setPrintConfigs(newConfigs);

        if (printConfigErrors[index]) {
            const updatedErrors = { ...printConfigErrors };
            delete updatedErrors[index][field as 'printerModel' | 'printerIp'];
            if (Object.keys(updatedErrors[index]).length === 0) {
                delete updatedErrors[index];
            }
            setPrintConfigErrors(updatedErrors);
        }
    }

    const validatePrintConfigs = (): {
        [key: number]: { printerModel?: string; printerIp?: string };
    } => {
        const errors: { [key: number]: { printerModel?: string; printerIp?: string } } = {};

        for (let i = 0; i < printConfigs.length; i++) {
            const config = printConfigs[i];
            const configErrors: { printerModel?: string; printerIp?: string } = {};

            if (!config.printerModel) {
                configErrors.printerModel = i18n.t('form.errors.required', {
                    name: 'Printer Model',
                });
            }
            if (!config.printerIp || config.printerIp.trim() === '') {
                configErrors.printerIp = i18n.t('form.errors.required', {
                    name: 'Printer IP',
                });
            }

            if (Object.keys(configErrors).length > 0) {
                errors[i] = configErrors;
            }
        }

        return errors;
    };

    const hasChanges = (): boolean => {
        return JSON.stringify(printConfigs) !== JSON.stringify(initialConfigs);
    };

    const isSaveDisabled = (): boolean => {
        return printConfigs.length === 0 || !hasChanges();
    };

    const handleSave = () => {
        const validationErrors = validatePrintConfigs();
        setPrintConfigErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        dispatch(actionCreators.setCategoryPrintConfigs(categoryId!, printConfigs));
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Typography variant="h6">{i18n.t('common.printerConfigurations')}</Typography>
            </DialogTitle>

            <DialogContent>
                <Box mt={1}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="subtitle1">
                            {i18n.t('common.addNewConfiguration')}
                        </Typography>
                        <IconButton color="primary" onClick={addPrintConfig} size="small">
                            <Add />
                        </IconButton>
                    </Box>

                    {printConfigs.map((config, index) => (
                        <Box key={index} mb={2} p={2} border="1px solid #e0e0e0" borderRadius={4}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={2}
                            >
                                <Typography variant="subtitle1">
                                    {i18n.t('common.configuration')} {index + 1}
                                </Typography>
                                <IconButton
                                    color="secondary"
                                    onClick={() => removePrintConfig(index)}
                                    size="small"
                                >
                                    <Delete />
                                </IconButton>
                            </Box>

                            <Box display="flex" style={{ gap: '16px' }}>
                                <FormControl
                                    variant="filled"
                                    style={{ flex: 1 }}
                                    error={!!printConfigErrors[index]?.printerModel}
                                >
                                    <InputLabel>{i18n.t('common.printerModel')}</InputLabel>
                                    <Select
                                        value={config.printerModel || ''}
                                        onChange={(e) =>
                                            updatePrintConfig(
                                                index,
                                                'printerModel',
                                                e.target.value as PrinterModel
                                            )
                                        }
                                    >
                                        {PRINTER_MODEL_OPTIONS.map((model) => (
                                            <MenuItem key={model} value={model}>
                                                {model}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {printConfigErrors[index]?.printerModel && (
                                        <Typography
                                            variant="caption"
                                            color="error"
                                            style={{ marginTop: 4 }}
                                        >
                                            {printConfigErrors[index].printerModel}
                                        </Typography>
                                    )}
                                </FormControl>

                                <TextField
                                    variant="filled"
                                    label={i18n.t('common.printerIPAddress')}
                                    type="string"
                                    value={config.printerIp}
                                    onChange={(e) =>
                                        updatePrintConfig(index, 'printerIp', e.target.value)
                                    }
                                    style={{ flex: 1 }}
                                    error={!!printConfigErrors[index]?.printerIp}
                                    helperText={printConfigErrors[index]?.printerIp || ' '}
                                    FormHelperTextProps={{
                                        style: {
                                            display: printConfigErrors[index]?.printerIp
                                                ? 'block'
                                                : 'none',
                                            marginLeft: 0,
                                        },
                                    }}
                                />

                                <TextField
                                    variant="filled"
                                    label={i18n.t('common.deviceID')}
                                    type="string"
                                    value={config.deviceId}
                                    onChange={(e) =>
                                        updatePrintConfig(index, 'deviceId', e.target.value)
                                    }
                                    style={{ flex: 1 }}
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
                <DialogActions style={{ marginTop: 10 }}>
                    <Button onClick={handleCancel} color="primary">
                        {i18n.t('button.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={isSaveDisabled()}
                    >
                        {i18n.t('button.save')}
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
